import type {
  GetListParams,
  GetListResult,
  Identifier,
  ResourceCallbacks,
} from "ra-core";

import {
  EXPENSE_MESSAGES,
  requireCancelReason,
  toExpenseWriteData,
} from "../../expenses/expenseRules";
import type {
  ExpenseFileKind,
  ExpenseFileMetadata,
} from "../../expenses/expenseTypes";
import {
  buildExpenseFilePath,
  EXPENSE_DOCUMENTS_BUCKET,
  EXPENSE_FILE_SIGNED_URL_EXPIRY_SECONDS,
  validateExpenseFile,
} from "../commons/expenseFiles";
import { getSupabaseClient } from "./supabase";

type MonthlyTotalsParams = Pick<GetListParams, "sort"> & Partial<GetListParams>;
type MonthlyTotalsGetList = (
  resource: string,
  params: never,
) => Promise<{ data: Record<string, unknown>[]; total?: number }>;

/**
 * Reads `expense_monthly_totals`, a view with no `id` column: a default `id`
 * sort becomes `month`, and each row gets the composite id FakeRest also builds.
 */
export const getExpenseMonthlyTotals = async (
  getList: MonthlyTotalsGetList,
  params: MonthlyTotalsParams,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<GetListResult<any>> => {
  const sort =
    params.sort?.field === "id"
      ? { ...params.sort, field: "month" }
      : params.sort;
  const result = await getList("expense_monthly_totals", {
    ...params,
    sort,
  } as never);
  return {
    ...result,
    data: result.data.map((row) => ({
      ...row,
      id: `${row.month}|${row.category_id}|${row.paid_by}`,
    })),
  };
};

/**
 * Almikia Epic EG (TASK-043): Supabase side of the expenses data layer
 * (TASK-042 schema). Lists and shows are read from `expenses_summary` (see the
 * data provider); `expense_monthly_totals` is read as-is. Writes go to the
 * `expenses` table, which has PER-COLUMN insert/update grants: the payload is
 * reduced to the editable columns (never id, iva_amount, created_*, updated_*,
 * cancelled_* or view-only fields) or PostgREST fails with 42501. A cancel goes
 * only through the `cancel_expense()` RPC, and files only ever get a NEW object
 * path (the bucket has no update policy, so never upsert).
 */

/** Columns `expense_categories` grants to signed-in users (insert: name; update: name, archived_at). */
const CATEGORY_WRITABLE_COLUMNS = ["name", "archived_at"] as const;

const pickCategoryColumns = (data: Record<string, unknown>) =>
  Object.fromEntries(
    CATEGORY_WRITABLE_COLUMNS.filter((column) => column in data).map(
      (column) => [column, data[column]],
    ),
  );

const denyDelete = async () => {
  throw new Error(EXPENSE_MESSAGES.noDelete);
};

/**
 * Lifecycle callbacks of the Supabase provider: strip writes to the granted
 * columns and refuse every delete (cancel instead; categories are archived).
 */
export const supabaseExpensesCallbacks: ResourceCallbacks[] = [
  {
    resource: "expenses",
    // TASK-044: the list's generic `q` search matches concept or supplier
    // (case-insensitive) on `expenses_summary`.
    beforeGetList: async (params) => {
      if (!params.filter?.q) return params;
      const { q, ...filter } = params.filter;
      return {
        ...params,
        filter: {
          ...filter,
          "@or": { "concept@ilike": q, "supplier@ilike": q },
        },
      };
    },
    beforeCreate: async (params) => ({
      ...params,
      data: toExpenseWriteData(params.data),
    }),
    beforeUpdate: async (params) => ({
      ...params,
      data: toExpenseWriteData(params.data),
    }),
    beforeDelete: denyDelete,
    beforeDeleteMany: denyDelete,
  },
  {
    resource: "expense_categories",
    beforeCreate: async (params) => ({
      ...params,
      data: pickCategoryColumns(params.data),
    }),
    beforeUpdate: async (params) => ({
      ...params,
      data: pickCategoryColumns(params.data),
    }),
    beforeDelete: denyDelete,
    beforeDeleteMany: denyDelete,
  },
];

export const createSupabaseExpensesMethods = () => ({
  /** `cancel_expense()`: the reason is mandatory; the row is kept and leaves the monthly totals. */
  async cancelExpense(id: Identifier, reason: string): Promise<void> {
    const trimmed = requireCancelReason(reason);
    const { error } = await getSupabaseClient().rpc("cancel_expense", {
      p_expense_id: id,
      p_reason: trimmed,
    });
    if (error) {
      console.error("cancel_expense.error", error);
      throw new Error(error.message || "No se pudo cancelar el gasto");
    }
  },

  /**
   * Uploads a comprobante / CFDI PDF / CFDI XML to the private
   * `expense-documents` bucket under a NEW unique path (never an upsert) and
   * returns the path to store on the matching expense column.
   */
  async uploadExpenseFile(
    kind: ExpenseFileKind,
    file: File,
  ): Promise<ExpenseFileMetadata> {
    const validation = validateExpenseFile(kind, file);
    if (!validation.valid) {
      throw Object.assign(new Error(validation.reason), {
        code: validation.reason,
      });
    }
    const path = buildExpenseFilePath(kind, file.name);
    const { error } = await getSupabaseClient()
      .storage.from(EXPENSE_DOCUMENTS_BUCKET)
      .upload(path, file, { upsert: false, contentType: file.type });
    if (error) {
      console.error("uploadExpenseFile.error", error);
      throw new Error("No se pudo subir el archivo");
    }
    return { path, file_name: file.name, mime_type: file.type };
  },

  /** A short-lived signed URL for an expense file (never a public URL). */
  async getExpenseFileUrl(path: string): Promise<string> {
    const { data, error } = await getSupabaseClient()
      .storage.from(EXPENSE_DOCUMENTS_BUCKET)
      .createSignedUrl(path, EXPENSE_FILE_SIGNED_URL_EXPIRY_SECONDS);
    if (error || !data) {
      console.error("getExpenseFileUrl.error", error);
      throw new Error("No se pudo generar el enlace de descarga");
    }
    return data.signedUrl;
  },
});
